import { useEffect, useState } from "react";
import { formatDepartureLead } from "@northstar/dispatch-core";
import type { PilotBoard } from "../pilotBoard";
import { weekAround } from "../pilotBoard";
import { DispatchInbox } from "./DispatchInbox";
import { EmptyState } from "./EmptyState";

export function TodayHome({
  board,
  day,
  onDay,
  onBrief,
  onLog,
  now: nowProp,
  unread = 0,
  onMarkRead,
  onReply,
}: {
  board: PilotBoard;
  day: string;
  onDay: (key: string) => void;
  onBrief: () => void;
  onLog: () => void;
  now?: Date;
  unread?: number;
  onMarkRead?: () => void;
  onReply?: (packet: string) => Promise<{ ok: true } | { ok: false; error: string }> | { ok: true } | { ok: false; error: string };
}) {
  const [ticked, setTicked] = useState(() => nowProp ?? new Date());
  useEffect(() => {
    if (nowProp) {
      setTicked(nowProp);
      return;
    }
    const id = window.setInterval(() => setTicked(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, [nowProp]);
  const now = nowProp ?? ticked;
  const stdMs = board.hero.stdIso ? Date.parse(board.hero.stdIso) : Number.NaN;
  const lead = Number.isFinite(stdMs) ? formatDepartureLead(stdMs - now.getTime()) : board.hero.lead;
  const week = weekAround(now, board.schedule.map((row) => row.departureIso));
  const selected = day || week.find((item) => item.isToday)?.key || "";
  const dayTrips = board.schedule.filter((row) => row.departureIso.slice(0, 10) === selected);
  return (
    <div data-testid="pilot-today">
      <section className="panel pilot-hero" data-testid="pilot-hero">
        <p className="kicker">Desk</p>
        <p data-testid="pilot-hero-fleet">
          {board.hero.active} active · {board.hero.onGround} ground · {board.hero.inAir} air
        </p>
        {board.hero.airborne.length ? (
          <p className="muted" data-testid="pilot-hero-ete">
            {board.hero.airborne
              .map((row) => `${row.registration} ETE ${row.eteLabel} ETA ${row.etaLabel} Zulu`)
              .join(" · ")}
          </p>
        ) : null}
        <p data-testid="pilot-hero-departure">
          {board.hero.stdIso
            ? `${lead} · ${board.hero.flight} ${board.hero.route} · STD ${board.hero.stdLabel}`
            : "No departure"}
        </p>
        <p className="pilot-hero-badges">
          <span
            className={board.hero.cautionCount ? "pilot-hero-badge is-caution is-pulse" : "pilot-hero-badge"}
            data-testid="pilot-hero-caution"
          >
            {board.hero.cautionCount} caution
          </span>
          <span
            className={board.hero.emergencyCount ? "pilot-hero-badge is-emergency is-pulse" : "pilot-hero-badge"}
            data-testid="pilot-hero-emergency"
          >
            {board.hero.emergencyCount} emergency
          </span>
        </p>
        {board.hero.alerts.length ? (
          <ul className="pilot-list" data-testid="pilot-hero-alerts">
            {board.hero.alerts.map((alert) => (
              <li key={alert.id}>
                {alert.source}: {alert.text}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      {board.crash ? (
        <section className="panel crash-banner" role="status" data-testid="crash-prompt">
          <p className="kicker">Restart</p>
          <h2>{board.crash.title}</h2>
          <p className="muted">{board.crash.body}</p>
        </section>
      ) : null}
      {!board.tripId ? (
        <EmptyState
          title="No assignment"
          body="No trips in the last pull. If the desk schedule has flights, that desk needs a Nanode sync token before they can reach this phone."
        />
      ) : (
        <article className="panel next-leg" data-testid="next-leg">
          <p className="kicker">Next leg</p>
          <div className="next-leg-head">
            <h1 className="trip-route">{board.route || "Route —"}</h1>
            <span
              className={`legality-dot is-${board.duty?.level ?? "ok"}`}
              data-testid="legality-dot"
              title={board.duty?.statusLabel || "No duty clock"}
            />
          </div>
          <p className="muted">
            {board.callsign}
            {board.tail ? ` · ${board.tail}` : ""}
            {board.aircraftName ? ` · ${board.aircraftName}` : ""}
          </p>
          <p>{board.std}</p>
          <div className="row">
            <button type="button" className="btn" data-testid="open-brief" onClick={onBrief}>
              Brief
            </button>
            <button type="button" className="btn ghost" data-testid="open-log" onClick={onLog}>
              Log
            </button>
          </div>
        </article>
      )}
      <div className="week-strip" data-testid="week-strip" role="listbox" aria-label="Week">
        {week.map((item) => (
          <button
            key={item.key}
            type="button"
            role="option"
            aria-selected={item.key === selected}
            className={item.key === selected ? "week-day is-selected" : "week-day"}
            data-today={item.isToday ? "true" : "false"}
            onClick={() => onDay(item.key)}
          >
            <span>{item.label}</span>
            {item.hasTrip ? <i className="week-mark" /> : null}
          </button>
        ))}
      </div>
      <ul className="pilot-list" data-testid="day-trips">
        {dayTrips.map((row) => (
          <li key={row.id}>
            <strong>
              {row.callsign} · {row.route}
            </strong>
            <span className="muted">
              {" "}
              {row.tail} · {row.label}
            </span>
          </li>
        ))}
      </ul>
      {board.tripId ? (
        <DispatchInbox board={board} unread={unread} onMarkRead={onMarkRead} onReply={onReply} />
      ) : null}
    </div>
  );
}
